import { Box, styled, Typography } from '@mui/material'
import { useCallback } from 'react'
import ReactCountdown, { CountdownRenderProps } from 'react-countdown'
import { useTranslation } from 'react-i18next'

const NodeBackground = styled(Box)`
  align-items: center;
  background: rgba(9, 8, 8, 0.7);
  border-radius: 12px;
  border: 4px solid rgba(255, 255, 255, 0.5);
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: center;
  position: relative;
  ${props => props.theme.breakpoints.up('res1024')} {
    height: 108px;
    width: 100px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    aspect-ratio: 1.075;
    max-width: 150px;
    width: 20vw;
  }
`

const NodeValue = styled(Typography)`
  font-style: normal;
  font-weight: 700;
  text-align: center;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 56px;
    line-height: 55px;
  }
  ${props => props.theme.breakpoints.between('res480', 'res1024')} {
    font-size: 50px;
    line-height: 50px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 30px;
    line-height: 30px;
  }
`

const NodeLabel = styled(Typography)`
  font-weight: 700;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 12px;
    line-height: 12px;
  }
  ${props => props.theme.breakpoints.between('res480', 'res1024')} {
    font-size: 16px;
    line-height: 16px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 12px;
    line-height: 12px;
  }
`

const CountdownNode = (props: NodeProps) => {
  const { label, loading, value } = props

  return (
    <NodeBackground className='Custom-Node-Background'>
      <NodeValue className='Custom-Node-Value'>
        {loading ? '-' : value}
      </NodeValue>
      <NodeLabel className='Custom-Node-Label'>{label}</NodeLabel>
    </NodeBackground>
  )
}

const Countdown = (props: Props) => {
  const { loading = false, targetDate, onComplete } = props

  const { t } = useTranslation()

  const handleOnRender = useCallback(
    (props: CountdownRenderProps) => {
      const { days, hours, minutes, seconds } = props

      return (
        <>
          <CountdownNode
            value={days}
            label={t('countdown.day')}
            loading={loading}
          />
          <CountdownNode
            value={hours}
            label={t('countdown.hour')}
            loading={loading}
          />
          <CountdownNode
            value={minutes}
            label={t('countdown.minute')}
            loading={loading}
          />
          <CountdownNode
            value={seconds}
            label={t('countdown.second')}
            loading={loading}
          />
        </>
      )
    },
    [loading, t]
  )

  return (
    <ReactCountdown
      key={targetDate.toString()}
      date={targetDate}
      renderer={handleOnRender}
      onComplete={onComplete}
    />
  )
}

type NodeProps = {
  label: string
  loading: boolean
  value: number
}

type Props = {
  loading?: boolean
  targetDate: string | number | Date
  onComplete?: () => void
}

export default Countdown
